<template>
  <div class="step-tab">
    <div class="cp-card">
      <p class="cp-hint">{{ L.contact_step_hint || 'The Contact step collects the booking contact. The form fields themselves come from Nezasa; here you can force the mobile number to be required and attach notes to individual fields.' }}</p>

      <BoxVisibilityToggle
          :boxVisibility="profile.box_visibility"
          boxKey="contact_mobile_required"
          :label="L.review_box_mobile_required || 'Mobile number required'"
          :hint="L.box_toggle_mobile_required_hint || 'The buyer must enter a mobile number to continue.'" />

      <h4 class="cp-subheading">{{ L.contact_notes_title || 'Field notes' }}</h4>
      <p class="cp-hint">{{ L.contact_notes_hint || 'Pick a field and write a note shown directly under it in the checkout, e.g. "You must be reachable at this number during the trip!" One note per field, per language.' }}</p>

      <div v-for="f in noteFields" :key="f" class="cp-note-row">
        <div class="cp-note-row__field">
          <label class="cp-label">{{ L.contact_notes_field || 'Field' }}</label>
          <CustomSelect
              :value="f"
              :options="fieldOptionsFor(f)"
              @input="v => v !== f && $emit('change-note-field', f, v)" />
        </div>
        <div class="cp-note-row__text">
          <CustomInput
              :label="L.contact_notes_text || 'Note text'"
              v-model="text[noteKey(f)]" />
        </div>
        <button type="button" class="cp-note-row__remove"
                :title="L.remove || 'Remove'"
                @click="$emit('remove-note-field', f)">
          <md-icon>delete_outline</md-icon>
        </button>
      </div>

      <p v-if="!noteFields.length" class="cp-empty">{{ L.contact_notes_empty || 'No field notes yet.' }}</p>

      <button type="button" class="cp-add-note" :disabled="!hasFreeField" @click="$emit('add-note-field')">
        <md-icon>add</md-icon>
        {{ L.contact_notes_add || 'Add field note' }}
      </button>
    </div>

    <PreviewFrame :label="L.preview || 'Preview'">
      <ContactPreview :texts="text" :lang="lang" :brandColor="brandColor" :boxVisibility="profile.box_visibility" />
    </PreviewFrame>
  </div>
</template>

<script>
import CustomInput from '../../parts/CustomInput.vue'
import CustomSelect from '../../parts/CustomSelect.vue'
import PreviewFrame from './previews/PreviewFrame.vue'
import ContactPreview from './previews/ContactPreview.vue'
import BoxVisibilityToggle from './BoxVisibilityToggle.vue'
import { CONTACT_NOTE_FIELDS, contactNoteKey } from './contactNoteFields'

/**
 * Contact step tab: the forced-required Mobile toggle (contact_mobile_required box)
 * plus the dynamic per-field notes editor. The parent owns WHICH fields have a note
 * (`noteFields`, shared across languages — add/remove/change go up as events); the
 * note TEXT is per language and binds straight into texts[lang] under
 * `contact_note_<field>`, so each language pill edits its own wording.
 */
export default {
  name: 'StepTabContact',
  components: { CustomInput, CustomSelect, PreviewFrame, ContactPreview, BoxVisibilityToggle },
  props: {
    options: { type: Object, required: true },
    profile: { type: Object, required: true },      // holds box_visibility (mutated in place)
    text: { type: Object, required: true },         // texts[lang] (mutated in place)
    noteFields: { type: Array, default: () => [] }, // field keys with a note row (parent-owned)
    lang: { type: String, default: 'de' },
    brandColor: { type: String, default: '#1a73e8' }
  },
  computed: {
    L() { return this.options.Lang || {}; },
    hasFreeField() {
      return CONTACT_NOTE_FIELDS.some(f => !this.noteFields.includes(f.key));
    }
  },
  methods: {
    noteKey(fieldKey) { return contactNoteKey(fieldKey); },
    /** Select options for one row: every field not used by ANOTHER row. */
    fieldOptionsFor(current) {
      return CONTACT_NOTE_FIELDS
        .filter(f => f.key === current || !this.noteFields.includes(f.key))
        .map(f => ({ value: f.key, label: this.L[f.langKey] || f.fallback }));
    }
  }
};
</script>

<style scoped>
.step-tab { animation: cp-fade .2s ease; }
@keyframes cp-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
.cp-card { background: #fff; border-radius: 1rem; box-shadow: 0 2px 12px rgba(0,0,0,0.06); padding: 1.5rem 1.75rem; }
.cp-hint { font-size: 0.8rem; color: #888; margin: 0 0 1rem; max-width: 620px; }
.cp-subheading { font-size: 0.95rem; font-weight: 600; color: #343841; margin: 1.5rem 0 0.35rem; }
.cp-label { display: block; font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 0.3rem; }
.cp-empty { font-size: 0.85rem; color: #aaa; font-style: italic; margin: 0 0 1rem; }

.cp-note-row {
  display: flex; align-items: flex-end; gap: 1rem;
  padding: 0.75rem 1rem; margin-bottom: 0.75rem;
  background: #f7f9fc; border: 1px solid #e7ecf3; border-radius: 0.75rem;
}
.cp-note-row__field { width: 220px; flex-shrink: 0; }
.cp-note-row__text { flex: 1; min-width: 0; }
.cp-note-row__remove {
  flex-shrink: 0; border: none; background: none; cursor: pointer;
  padding: 0.4rem; margin-bottom: 0.25rem; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.cp-note-row__remove:hover { background: #fdecea; }
.cp-note-row__remove .md-icon { color: #c0392b; }

.cp-add-note {
  display: inline-flex; align-items: center; gap: 0.35rem;
  border: 1px dashed #c9d2de; background: #fff; color: #4a5568;
  border-radius: 0.75rem; padding: 0.5rem 1rem; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; transition: border-color .15s ease, background .15s ease;
}
.cp-add-note:hover:not(:disabled) { border-color: #9aa7b8; background: #f7f9fc; }
.cp-add-note:disabled { opacity: 0.5; cursor: not-allowed; }
.cp-add-note .md-icon { font-size: 18px !important; color: inherit; }
</style>
